// Vistoria do veículo (pedido 2026-10-08) — o motorista faz pelo app
// (Perfil → Vistoria); a equipe vê em Vistorias. Backend:
// src/vehicle-inspections no gpsdrive-backend.
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut, gpsDriveUpload } from "@/lib/api/gpsdrive-client";

export type InspectionItemStatus = "ok" | "problema" | "na";
export type FuelLevel = "reserva" | "1/4" | "1/2" | "3/4" | "cheio";

export interface InspectionChecklistItem {
  key: string;
  label: string;
  status: InspectionItemStatus;
  note?: string;
}

export interface InspectionPhoto {
  id: string;
  label: string | null;
  url: string;
  thumbUrl: string;
  createdAt: string;
}

export interface VehicleInspection {
  id: string;
  userId: string;
  userName: string;
  traccarDeviceId: number;
  vehicleName: string;
  plate: string | null;
  odometerKm: number | null;
  fuelLevel: FuelLevel | null;
  checklist: InspectionChecklistItem[];
  notes: string | null;
  hasIssues: boolean;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  // Hora em que foi feita no celular (offline pode ser antes do envio).
  performedAt?: string;
  photos: InspectionPhoto[];
}

export interface CreateInspectionInput {
  traccarDeviceId: number;
  odometerKm?: number | null;
  fuelLevel?: FuelLevel | null;
  checklist: InspectionChecklistItem[];
  notes?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  // Modo offline (2026-10-16): id gerado no celular (reenvio não duplica)
  // e hora em que a vistoria foi feita.
  clientId?: string;
  performedAt?: string;
}

// Checklist da empresa (Gestão de Frota → Vistorias → Checklist). Sem
// checklist salvo, o backend devolve o padrão (isDefault).
export type InspectionItemPhotoMode = "none" | "optional" | "required";

export interface InspectionTemplateItem {
  key: string;
  label: string;
  photo: InspectionItemPhotoMode;
}

export interface InspectionTemplatePhoto {
  key: string;
  label: string;
  required: boolean;
}

export interface InspectionTemplateInput {
  items: InspectionTemplateItem[];
  photos: InspectionTemplatePhoto[];
  requireOdometer: boolean;
  requireFuel: boolean;
}

export interface InspectionTemplate extends InspectionTemplateInput {
  isDefault: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export const INSPECTION_ITEM_PHOTO_LABELS: Record<InspectionItemPhotoMode, string> = {
  none: "Sem foto",
  optional: "Foto opcional",
  required: "Foto obrigatória",
};

// "Foto ponto" (pedido 2026-10-08): selfie do motorista mostrando a placa.
// Liga/desliga no editor do Checklist; guardada como uma foto do checklist
// com esta chave fixa (sem coluna nova no backend) e mostrada à parte.
export const DRIVER_SELFIE_PHOTO = { key: "foto_ponto", label: "Foto ponto", required: true } as const;

// Limite do backend (MAX_PHOTOS_PER_INSPECTION).
export const MAX_INSPECTION_PHOTOS = 40;

export const FUEL_LEVEL_LABELS: Record<FuelLevel, string> = {
  reserva: "Reserva",
  "1/4": "1/4",
  "1/2": "1/2",
  "3/4": "3/4",
  cheio: "Cheio",
};

export const vehicleInspectionsService = {
  access: () => gpsDriveGet<{ create: boolean; view: boolean }>("/api/v1/vehicle-inspections/access"),
  mine: () => gpsDriveGet<VehicleInspection[]>("/api/v1/vehicle-inspections/mine"),
  list: (params: { deviceId?: number; userId?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.deviceId) q.set("deviceId", String(params.deviceId));
    if (params.userId) q.set("userId", params.userId);
    const qs = q.toString();
    return gpsDriveGet<VehicleInspection[]>(`/api/v1/vehicle-inspections${qs ? `?${qs}` : ""}`);
  },
  create: (input: CreateInspectionInput) =>
    gpsDrivePost<VehicleInspection>("/api/v1/vehicle-inspections", input),
  uploadPhoto: (id: string, file: File, label: string) =>
    gpsDriveUpload<InspectionPhoto>(
      `/api/v1/vehicle-inspections/${id}/photos?label=${encodeURIComponent(label)}`,
      file,
    ),
  template: () => gpsDriveGet<InspectionTemplate>("/api/v1/vehicle-inspections/template"),
  saveTemplate: (input: InspectionTemplateInput) =>
    gpsDrivePut<InspectionTemplate>("/api/v1/vehicle-inspections/template", input),
  resetTemplate: () => gpsDriveDelete<InspectionTemplate>("/api/v1/vehicle-inspections/template"),
  remove: (id: string) => gpsDriveDelete<void>(`/api/v1/vehicle-inspections/${id}`),
};

// Vistoria por link público (2026-10-23): o escritório gera um código pro
// motorista (Gestão de Frota → Motoristas, ícone ao lado do nome) e ele faz
// a vistoria em /vistoria pelo navegador, sem conta.
export type InspectionLinkStatus = "pendente" | "usado" | "expirado" | "cancelado";

export interface InspectionLink {
  id: string;
  code: string;
  traccarDeviceId: number;
  vehicleName: string;
  plate: string | null;
  driverName: string;
  driverPhone: string | null;
  createdByName: string | null;
  expiresAt: string;
  usedAt: string | null;
  inspectionId: string | null;
  status: InspectionLinkStatus;
  createdAt: string;
}

export interface CreatedInspectionLink extends InspectionLink {
  link: string;
  message: string;
  whatsapp: { sent: boolean; error: string | null } | null;
}

export interface CreateInspectionLinkInput {
  fleetDriverId?: string;
  traccarDeviceId?: number;
  driverName?: string;
  driverPhone?: string | null;
  validHours?: number;
  send?: boolean;
  origin?: string;
}

export interface PublicInspectionView {
  code: string;
  companyName: string | null;
  vehicleName: string;
  plate: string | null;
  driverName: string;
  expiresAt: string;
  used: boolean;
  template: InspectionTemplateInput;
  inspection: VehicleInspection | null;
}

// Fora de http(s) (app nativo) o link usa o domínio principal.
export const inspectionLinkOrigin = () =>
  /^https?:/.test(window.location.origin) ? window.location.origin : "https://gpsdrive.com.br";

export const vehicleInspectionLinksService = {
  list: () => gpsDriveGet<InspectionLink[]>("/api/v1/vehicle-inspections/links"),
  create: (input: CreateInspectionLinkInput) =>
    gpsDrivePost<CreatedInspectionLink>("/api/v1/vehicle-inspections/links", { origin: inspectionLinkOrigin(), ...input }),
  cancel: (id: string) => gpsDrivePost<InspectionLink>(`/api/v1/vehicle-inspections/links/${id}/cancel`),
};

const PUBLIC = "/api/v1/public/vehicle-inspections";
export const publicInspectionService = {
  get: (code: string) => gpsDriveGet<PublicInspectionView>(`${PUBLIC}/${encodeURIComponent(code)}`),
  submit: (code: string, input: Omit<CreateInspectionInput, "traccarDeviceId" | "clientId">) =>
    gpsDrivePost<VehicleInspection>(`${PUBLIC}/${encodeURIComponent(code)}`, input),
  uploadPhoto: (code: string, file: File, label: string) =>
    gpsDriveUpload<InspectionPhoto>(`${PUBLIC}/${encodeURIComponent(code)}/photos?label=${encodeURIComponent(label)}`, file),
};
